import { IkeFieldType } from '@ikegps/core-api';
import { IkeId } from '../../../id';
import type { FormModelField } from '../../form/form.field';
import type { FormModelFieldSubForm } from '../../form/subform/form.subform';
import { CollectionModelFieldSubFormInstance } from '../collection.base';
import { CollectionModelField, ModelMutator } from '../collection.field';
import { registerFieldType } from './field.registry';

export class ModelMutatorSubForm extends ModelMutator<FormModelField, CollectionModelFieldSubForm> {
	/**
	 * Create a new subForm instance
	 * @param id (Optional) instanceId
	 * @returns the instanceId of the instance created.
	 */
	create(id?: string): string {
		if (!id) {
			id = IkeId.generate(10);
		}

		const instanceIds = this.getStateValue();

		if (instanceIds.indexOf(id) !== -1) {
			throw new Error(`Subform instance id must be unique: ${id}`);
		}

		instanceIds.push(id);

		this.state.setFieldValue(this.field.id, instanceIds);

		return id;
	}

	override clear() {
		for (const instance of this.field.value) {
			this.clearInstance(instance);
		}

		this.state.setFieldValue(this.field.id, null);
	}

	remove(instanceId: string): void {
		const instanceIds = this.getStateValue();
		const index = instanceIds.indexOf(instanceId);
		if (index === -1 || !this.field.value[index]) {
			throw new Error(`Cannot remove subform. Instance "${instanceId}" is missing from subform "${this.field.id}"`);
		}

		this.clearInstance(this.field.value[index]);

		instanceIds.splice(index, 1);
		delete this.field.instances[instanceId];
		this.field.collection.state.setFieldValue(this.field.id, instanceIds);
	}

	private getStateValue(): string[] {
		const stateValue = this.field.stateValue;
		if (Array.isArray(stateValue)) {
			return stateValue.slice();
		}

		return [];
	}

	private clearInstance(instance: CollectionModelFieldSubFormInstance) {
		for (const fieldSchema of instance.schema.fields) {
			if (instance.hasField(fieldSchema.id)) {
				instance.getField(fieldSchema.id).Mutator.clear();
			}
		}
	}

	/**
	 * Copies all subform instances from the target into this subform.
	 *
	 * @param from Subform to copy from.
	 */
	override copy(from: CollectionModelFieldSubForm): boolean {
		this.clear();

		// When copying within the same collection (duplication), generate new instance IDs
		// to avoid sharing state keys with the original. When copying across collections
		// (span linking), preserve instance IDs so proxy state dirty detection works correctly.
		// UNLESS the ID is already active in the target collection (e.g., a previous link
		// operation copied the same IDs onto another span). Reusing the ID in that case would
		// cause two spans to share state keys, so a fresh ID is generated instead.
		const sameCollection = this.field.collection === from.collection;

		let hasCopied = true;
		for (const instance of from.value) {
			const preserveId = !sameCollection && !this.isInstanceIdActive(instance.instanceId);
			const createdInstanceId = preserveId ? this.create(instance.instanceId) : this.create();
			const newInstance = this.field.getInstance(createdInstanceId)!;

			const fieldCopied = this.copyFields(instance, newInstance);
			if (!fieldCopied) {
				hasCopied = false;
			}
		}

		return hasCopied;
	}

	/**
	 * Returns true if the given instance ID has any active (non-null) field values in the
	 * target collection's current state. This detects when an ID is already in use by a
	 * different subform instance and cannot be safely reused without sharing state keys.
	 */
	private isInstanceIdActive(instanceId: string): boolean {
		const state = this.field.collection.state;
		for (const fieldSchema of this.field.schema.fields) {
			if (fieldSchema && state.hasFieldValue(instanceId + ':' + fieldSchema.id)) {
				return true;
			}
		}
		return false;
	}

	/**
	 * Copies a specific subform instance from the target into this subform.
	 *
	 * @param from Subform to copy from.
	 * @param instanceId Specific instance to copy.
	 */
	copyFromInstance(fromInstance: CollectionModelFieldSubFormInstance): CollectionModelFieldSubFormInstance | null {
		const newInstanceId = this.create();
		const newInstance = this.field.getInstance(newInstanceId);

		if (!newInstance) {
			return null;
		}

		const hasCopied = this.copyFields(fromInstance, newInstance);
		return hasCopied ? newInstance : null;
	}

	private copyFields(from: CollectionModelFieldSubFormInstance, to: CollectionModelFieldSubFormInstance): boolean {
		let hasCopied = true;
		for (const schemaField of from.schema.fields) {
			const field = from.getField(schemaField.id);
			const newField = to.getField(schemaField.id);
			const fieldCopied = newField.Mutator.copy(field);
			if (!fieldCopied) {
				hasCopied = false;
			}
		}
		return hasCopied;
	}
}

export class CollectionModelFieldSubForm extends CollectionModelField<FormModelFieldSubForm, string[]> {
	static TYPE: IkeFieldType = IkeFieldType.FORM;
	static isSubFormField(f: CollectionModelField): f is CollectionModelFieldSubForm {
		return f.schema.type === CollectionModelFieldSubForm.TYPE;
	}

	instances: { [instanceId: string]: CollectionModelFieldSubFormInstance } = {};

	private _Mutator: ModelMutatorSubForm | null = null;
	get Mutator() {
		if (this._Mutator == null) {
			this._Mutator = new ModelMutatorSubForm(this);
		}
		return this._Mutator;
	}

	override get value(): CollectionModelFieldSubFormInstance[] {
		const stateValue = this.stateValue;
		if (!Array.isArray(stateValue)) {
			return [];
		}
		const output: CollectionModelFieldSubFormInstance[] = [];
		for (const instanceId of stateValue) {
			output.push(this.getOrCreateInstance(instanceId));
		}
		return output;
	}

	protected getOrCreateInstance(instanceId: string): CollectionModelFieldSubFormInstance {
		let existing = this.instances[instanceId];
		if (existing == null) {
			existing = this.instances[instanceId] = new CollectionModelFieldSubFormInstance(this, instanceId, this.schema);
		}
		return existing;
	}

	getInstance(instanceId: string): CollectionModelFieldSubFormInstance | null {
		const stateValue = this.stateValue;
		if (!Array.isArray(stateValue)) {
			return null;
		}
		const hasInstance = stateValue.indexOf(instanceId) > -1;
		if (hasInstance) {
			return this.getOrCreateInstance(instanceId);
		}
		return null;
	}

	mustGetInstance(instanceId: string): CollectionModelFieldSubFormInstance {
		const instance = this.getInstance(instanceId);
		if (!instance) {
			throw new Error(`mustGetInstance: failed to get instance with id ${instanceId}`);
		}
		return instance;
	}

	hasValue(): boolean {
		const stateValue = this.stateValue;
		if (!Array.isArray(stateValue)) {
			return false;
		}
		return this.stateValue.length > 0;
	}
}

registerFieldType(
	IkeFieldType.FORM,
	(parent, schema) => new CollectionModelFieldSubForm(parent, <FormModelFieldSubForm>schema)
);
